import { memo } from 'react'
import Link from 'next/link'
import Image from './Image'
import WalletThumbnail from './WalletThumbnail'
import useAddresses from 'api/hooks/useAddresses'
import { getAvatarURL, isAvatarVerified } from 'helpers/avatar'
import { classNames } from 'helpers/utilities'
import type { NfdRecord } from 'api/api-client'

type Size = 'sm' | 'md'

interface UserThumbnailInterface {
  className?: string
  fallbackClassName?: string
  link?: boolean
  padded?: boolean
  size?: Size
  showAddressFallback?: boolean
}

const Skeleton = ({ size }: { size: Size }) => (
  <div className="animate-pulse flex items-center space-x-2">
    <div className="flex-shrink-0">
      <div
        className={classNames(size === 'md' ? 'h-8 w-8' : 'h-5 w-5', 'bg-gray-100 rounded-full')}
      />
    </div>
    <div className="flex-1">
      <div className="h-3 bg-gray-100 rounded-full max-w-[6rem]" />
    </div>
  </div>
)

type UserThumbnailProps = (
  | {
      nfd: NfdRecord
      address?: never
      staleTime?: never
    }
  | {
      nfd?: never
      address: string
      staleTime?: number
    }
) &
  UserThumbnailInterface

function UserThumbnail({
  nfd: _nfd,
  address = '',
  className = '',
  fallbackClassName = '',
  link = true,
  padded: isPadded = false,
  size = 'md',
  showAddressFallback = true,
  staleTime = 0
}: UserThumbnailProps) {
  const { data, isLoading, error } = useAddresses({
    params: {
      address: [address],
      limit: 1,
      view: 'thumbnail'
    },
    options: {
      enabled: address !== '',
      refetchOnWindowFocus: false,
      staleTime
    }
  })

  if (isLoading) {
    return <Skeleton size={size} />
  }

  const nfd = _nfd || data?.[0]

  if (!showAddressFallback && (error || !nfd)) {
    return null
  }

  if (error || !nfd) {
    return <WalletThumbnail address={address} className={fallbackClassName} />
  }

  const AvatarImage = () => (
    <Image
      className="rounded-full object-cover w-full h-full"
      src={getAvatarURL(nfd)}
      alt={nfd.name}
      layout="fill"
      objectFit="cover"
      fallbackSrc="/img/nfd-loading-placeholder.jpg"
      loadingSrc="/img/nfd-loading-placeholder.jpg"
    />
  )

  const renderAvatar = () => {
    const isVerified = isAvatarVerified(nfd)

    if (isVerified) {
      return (
        <div
          className={classNames(
            size === 'md' ? ' w-10 h-10' : 'w-7 h-7',
            'flex items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-pink-500 w-10 h-10'
          )}
        >
          <div
            className={classNames(
              size === 'md' ? 'h-8 w-8' : 'h-5 w-5',
              'relative rounded-full bg-gray-200 overflow-hidden ring-2 ring-white'
            )}
          >
            <AvatarImage />
          </div>
        </div>
      )
    }

    if (isPadded) {
      return (
        <div className="flex items-center justify-center rounded-full bg-transparent w-10 h-10">
          <div
            className={classNames(
              size === 'md' ? 'h-8 w-8' : 'h-5 w-5',
              'relative rounded-full bg-gray-200 overflow-hidden ring-2 ring-transparent'
            )}
          >
            <AvatarImage />
          </div>
        </div>
      )
    }

    return (
      <div
        className={classNames(
          size === 'md' ? 'h-8 w-8' : 'h-5 w-5',
          'relative rounded-full bg-gray-200 overflow-hidden'
        )}
      >
        <AvatarImage />
      </div>
    )
  }

  const renderThumbnail = () => (
    <div className="flex items-center space-x-2">
      <div className="flex-shrink-0">{renderAvatar()}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{nfd.name}</p>
      </div>
    </div>
  )

  if (!link) {
    return <div className={className}>{renderThumbnail()}</div>
  }

  return (
    <Link href={`/name/${nfd.name}`}>
      <a className={classNames('outline-brand-500 rounded-md', className)}>{renderThumbnail()}</a>
    </Link>
  )
}

export default memo(UserThumbnail)
